import { Usuario } from "../../types";

// ════════════════════════════════════════════════════════════════
// 🔐 B88 (AI_98) — gates de categoria profissional no FRONTEND
// ════════════════════════════════════════════════════════════════
// Espelham as funções homônimas das firestore.rules. A fechadura real é a
// regra do banco; aqui é experiência de uso (esconder o que o banco negaria,
// para ninguém esbarrar em erro de permissão ou gerar PDF que não salva).
// Doc legado sem `categoria` = "medico" (mesmo default do AuthContext e das
// rules). Manter as duas pontas em sincronia ao mudar a matriz de acesso.

const CATEGORIAS_MULTIPROFISSIONAIS = [
  "enfermeiro",
  "psicologo",
  "nutricionista",
  "farmaceutico",
];

function categoriaDe(usuario: Usuario | null): string {
  return usuario?.categoria || "medico";
}

/**
 * Espelho de canWriteClinical() das rules: admin ou usuario (qualquer
 * categoria). Falso para observador e convidado — papéis somente leitura.
 * Usar nas telas sem granularidade fina (exames, sorologias, marcadores,
 * imagem, pendências, agenda, ficha do paciente).
 */
export function podeEscreverClinico(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  return usuario.tipo === "administrador" || usuario.tipo === "usuario";
}

/**
 * 🔐 B323 Bloco 3 (AI_145) — é o administrador (médico desenvolvedor-responsável)?
 * Espelho de isAdmin() das rules (tipo == 'administrador'). Usado como ESCAPE de
 * correção: após a janela de 24h, o registro clínico trava para a equipe, mas o
 * admin mantém edição/exclusão no app para casos excepcionais (ex.: o lab com data
 * errada descoberto depois — caso B296 — sem precisar do console do Firebase).
 * Manter em sincronia com isAdmin() nas firestore.rules.
 */
export function ehAdministrador(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  return usuario.tipo === "administrador";
}

/** Ato médico: prescrever, pedir exames, laudar, gerar IA, calibrar semáforo. */
export function ehMedico(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  return (
    usuario.tipo === "administrador" ||
    (usuario.tipo === "usuario" && categoriaDe(usuario) === "medico")
  );
}

/** Multiprofissional de saúde: evoluções + relatório multidisciplinar. */
export function ehMultiprofissional(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  return (
    usuario.tipo === "usuario" &&
    CATEGORIAS_MULTIPROFISSIONAIS.includes(categoriaDe(usuario))
  );
}

/**
 * 🩺 B252 (AI_114) — Documentos PRÓPRIOS do serviço de enfermagem (ex.: Declaração
 * de Agendamento). Área EXCLUSIVA da enfermagem: a enfermeira é a AUTORA do
 * documento (não é prescrição médica nem encaminhamento do médico). Admin entra
 * por ser superusuário do sistema. ESPELHO da fechadura real das firestore.rules
 * (isEnfermeiro() na coleção documentos-enfermagem) — manter em sincronia.
 */
export function ehEnfermeiro(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  return (
    usuario.tipo === "administrador" ||
    (usuario.tipo === "usuario" && categoriaDe(usuario) === "enfermeiro")
  );
}

/**
 * 🔐 B243 (AI_110): pode usar "Importar PDF" de exames (etapa de IA
 * "extrair-exames-lab")? É captação de dado conferida na tela antes de salvar
 * — NÃO é geração de laudo. Liberada a médico/admin + ENFERMEIRO e
 * ADMINISTRATIVO (decisão de Leonardo). ESPELHO da fechadura real do backend
 * (api/_lib/autorizarEtapaIA.js → ETAPAS_EQUIPE_NAO_MEDICA). Manter em sincronia.
 */
const CATEGORIAS_IMPORTAM_EXAMES = ["enfermeiro", "administrativo"];
export function podeImportarExamesPDF(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  if (ehMedico(usuario)) return true;
  return (
    usuario.tipo === "usuario" &&
    CATEGORIAS_IMPORTAM_EXAMES.includes(categoriaDe(usuario))
  );
}

/**
 * 🔐 B76: gere a agenda (marca/remarca/cancela para TODOS os profissionais
 * e configura grades)? Admin, enfermagem e administrativo.
 */
export function gereAgenda(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  return (
    usuario.tipo === "administrador" ||
    (usuario.tipo === "usuario" &&
      ["enfermeiro", "administrativo"].includes(categoriaDe(usuario)))
  );
}

/**
 * 🔐 B76: enxerga a agenda INTEIRA? Quem a gere + papéis de leitura global
 * (observador/convidado). Médico e demais profissionais de agenda própria
 * recebem da fonte só os próprios registros (a query espelha a regra).
 */
export function veTodaAgenda(usuario: Usuario | null): boolean {
  if (!usuario) return false;
  return gereAgenda(usuario) || usuario.tipo !== "usuario";
}

/**
 * Pode editar/excluir uma prescrição do histórico?
 * Médico: qualquer uma. Multiprofissional: só o PRÓPRIO relatório
 * multidisciplinar (espelho da regra de prescricoes).
 */
export function podeGerirPrescricao(
  usuario: Usuario | null,
  presc: { subtipo?: string; criadoPor?: string }
): boolean {
  if (ehMedico(usuario)) return true;
  return (
    ehMultiprofissional(usuario) &&
    presc.subtipo === "relatorio_multi" &&
    !!usuario?.id &&
    presc.criadoPor === usuario.id
  );
}
